import React from 'react';

import { type UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useListGateways } from 'src/features/gateway/api/useListGateways';
import { gatewayMessages } from 'src/features/gateway/utils/messages';
import { IDENTIFIER_TYPE } from 'src/features/membership/types';
import { IDENTIFIER_TYPE_TRANSLATION_MAP } from 'src/features/membership/utils/constants';
import { identifiersMessages } from 'src/features/membership/utils/messages';
import { VisitorFormSchemaValues } from 'src/features/visitor/utils/formSchema';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { useListZones } from 'src/features/zone/api/useListZones';
import { zoneMessages } from 'src/features/zone/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import DatePickerWithTime from 'src/registry/ui/DatePickerWithTime';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS } from 'src/utils/constants';
import { transliterateUppercase } from 'src/utils/regex';
import { translate } from 'src/utils/translate';

type VisitorActionFormProps = {
    form: UseFormReturn<VisitorFormSchemaValues>;
    onSubmit: (values: VisitorFormSchemaValues) => void;
};
const GATEWAY_ORDERING = 'name';

export default function VisitorForm({ form, onSubmit }: VisitorActionFormProps) {
    const { formatMessage, locale } = useIntl();

    const { parkingId } = useParking();
    const { data: zoneData } = useListZones({ parkingId });
    const { zone: entry_zone, identifier_type: identifierType } = form.watch();

    React.useEffect(() => {
        if (form.formState.isDirty) form.setValue('identifier_value', '');
    }, [identifierType, form]);

    const { data: gatewayData } = useListGateways({
        parkingId,
        ordering: GATEWAY_ORDERING,
        entry_zone,
    });

    const zoneOptions = React.useMemo(() => {
        if (!zoneData) return [];
        return zoneData.results.map(({ translations, id }) => ({
            label: translate(translations, locale)?.name || '',
            value: id,
        }));
    }, [zoneData, locale]);

    const gatewayOptions = React.useMemo(() => {
        if (!gatewayData) return [];
        return gatewayData.results.map(({ translations, id }) => ({
            label: translate(translations, locale)?.name || '',
            value: id,
        }));
    }, [gatewayData, locale]);

    React.useEffect(() => {
        if (zoneOptions.length === 1) form.setValue('zone', zoneOptions[0].value);
        if (gatewayOptions.length === 1) form.setValue('gateway', gatewayOptions[0].value);
    }, [form, gatewayOptions, zoneOptions]);

    return (
        <Form form={form} variant="dialog" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
                control={form.control}
                name="entry_time"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(visitorMessages.entryTime)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <DatePickerWithTime dateISO={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<VisitorFormSchemaValues>
                form={form}
                name="identifier_type"
                label={formatMessage(identifiersMessages.identifierType)}
                options={[IDENTIFIER_TYPE.LICENSE_PLATE, IDENTIFIER_TYPE.ACCESS_KEY]}
                optionTranslationMap={IDENTIFIER_TYPE_TRANSLATION_MAP}
            />
            <FormField
                control={form.control}
                name="identifier_value"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(identifiersMessages.identifier)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    {...field}
                                    onChange={(e) => {
                                        const value =
                                            identifierType === IDENTIFIER_TYPE.LICENSE_PLATE
                                                ? transliterateUppercase(e.target.value.toLocaleUpperCase())
                                                : e.target.value;
                                        field.onChange(value);
                                    }}
                                    value={field.value ?? ''}
                                    placeholder={`${formatMessage(identifiersMessages.identifier)}...`}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<VisitorFormSchemaValues>
                form={form}
                name="zone"
                label={formatMessage(zoneMessages.zone)}
                options={zoneOptions}
                onChange={() => form.resetField('gateway')}
            />
            <SelectFormField<VisitorFormSchemaValues>
                form={form}
                name="gateway"
                label={formatMessage(gatewayMessages.gateway)}
                options={gatewayOptions}
            />
            <SelectFormField<VisitorFormSchemaValues>
                form={form}
                name="open_gateway"
                isBoolean
                label={formatMessage(visitorMessages.openGateway)}
                options={BOOLEAN_OPTIONS}
                optionTranslationMap={BOOLEAN_MAP}
                required={false}
            />
        </Form>
    );
}
